import { useApiPost } from '@/api/main/query'
import { TOKEN_KEY } from '@/features/auth'
import { authAPI } from '@/features/auth/api/endpoints'
import { ThemeToggle } from '@/features/themeToggle/ui/ThemeToggle'
import {
  BaseButton,
  BaseCheckbox,
  BaseInput,
  PasswordIndicator,
  PasswordInput,
} from '@/shared/components'
import MainIcon from '@/shared/components/icons/MainIcon'
import {
  confirmPasswordValidation,
  getPasswordStrength,
  passwordValidation,
} from '@/shared/constants/validationRules'
import { useToast } from '@/shared/hooks'
import { setStorageItem } from '@/shared/lib/storage/localStorage'
import { useSupportContact } from '@/entities/domain/hooks/useSupportContact'
import { Controller, FieldError, FieldValues, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect } from 'react'

const Register = () => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm()
  const { addToast } = useToast()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { telegramUrl, displayName } = useSupportContact()

  // Обработка referall
  useEffect(() => {
    const referralCode = searchParams.get('referall')
    if (referralCode) {
      setValue('referralCode', referralCode)
    }
  }, [searchParams, setValue])

  interface UserData {
    token: string
    expiresAtUtc: string
  }

  interface sendData {
    email: string
    password: string
    role: number
    referralCode: string
    teamName: string
  }

  const { mutate: signup, isPending } = useApiPost<UserData, sendData>(authAPI.signup, {
    onSuccess: (value) => {
      addToast('Успех!', 'Аккаунт создан', 'success')
      if (value?.token) {
        setStorageItem(TOKEN_KEY, value.token)
        navigate('/dashboard')
      } else {
        addToast('Ошибка!', 'Ошибка регистрации: сервер не вернул токен', 'error')
      }
      reset()
    },
    onError: (error) => {
      addToast('Ошибка!', error.message || 'Ошибка регистрации', 'error')
    },
  })

  const onSubmit = async (data: FieldValues) => {
    if (data.password !== data.confirmPassword) {
      addToast('Ошибка!', 'Пароли не совпадают', 'error')
      return
    }
    const { email, password, referralCode, teamName } = data
    signup({ email, password, role: 2, referralCode: referralCode || '', teamName })
  }

  const password = watch('password', '')
  const strength = getPasswordStrength(password)

  return (
    <div className="flex flex-col items-center gap-y-20 py-5 w-full max-w-[350px]">
      <ThemeToggle />
      <div className="flex justify-between items-center w-full">
        <MainIcon />
        <h2 className="text-text-secondary dark:text-primary text-xl">Регистрация</h2>
      </div>
      <div className="w-full">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col space-y-4">
            <BaseInput
              {...register('teamName', {
                required: 'Поле обязательно',
                minLength: { value: 4, message: 'Мин. 4 символа' },
              })}
              error={(errors.username as FieldError)?.message}
              placeholder="Название команды"
              size="md"
            />
            <BaseInput
              placeholder="Почта"
              {...register('email', {
                required: 'Поле обязательно',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Некорректный email',
                },
              })}
              error={(errors.email as FieldError)?.message}
              size="md"
            />
            <PasswordInput
              placeholder="Пароль"
              {...register('password', passwordValidation)}
              error={(errors.password as FieldError)?.message}
              size="md"
            />
            <PasswordInput
              placeholder="Повторите пароль"
              {...register('confirmPassword', confirmPasswordValidation(password))}
              error={(errors.confirmPassword as FieldError)?.message}
              size="md"
            />
            <div className="h-6">
              {password.length > 0 && <PasswordIndicator strength={strength} />}
            </div>
            <div className="pt-4">
              <BaseInput
                variant="secondary"
                label="Реферальный код"
                placeholder="Введите код"
                optionInputClasses="!text-primary dark:!text-primary-dark"
                // rules={{ required: 'Поле обязательно' }}
                {...register('referralCode')}
                error={(errors.referralCode as FieldError)?.message}
                size="md"
              />
            </div>
            <div className="flex items-center py-4">
              <Controller
                name="agreeToTerms"
                control={control}
                // rules={{ required: 'Поле обязательно' }}
                render={({ field }) => (
                  <BaseCheckbox
                    {...field}
                    checked={field.value}
                    error={(errors.agreeToTerms as FieldError)?.message}
                    label={
                      <span className="text-text-main dark:text-text-secondary">
                        Я согласен с{' '}
                        <a href="#" className="text-primary dark:text-primary-dark">
                          правилами пользования
                        </a>
                      </span>
                    }
                  />
                )}
              />
            </div>
            <BaseButton type="submit" padding="lg" isLoading={isPending}>
              Зарегистрироваться
            </BaseButton>
            <BaseButton variant="outlined" padding="lg" onClick={() => navigate('/login')}>
              Войти в существующий аккаунт
            </BaseButton>
          </div>
        </form>
      </div>
      <div className="text-center">
        <span className="text-text-main dark:text-text-secondary text-sm">
          Контакт поддержки:{' '}
          <a href={telegramUrl} target="_blank" rel="noopener noreferrer">
            <span className="text-primary dark:text-primary-dark text-sm">{displayName}</span>
          </a>
        </span>
      </div>
    </div>
  )
}

export default Register
